import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  ShieldCheck,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Search,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import { useIsAppAdmin } from "../hooks/useIsAppAdmin";

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

type ClubRole = "dirigeant" | "cadre_technique" | "coach" | "joueur" | "lecteur";

const ROLE_LABEL: Record<ClubRole, string> = {
  dirigeant: "Dirigeant",
  cadre_technique: "Cadre technique",
  coach: "Coach",
  joueur: "Joueur",
  lecteur: "Lecteur",
};

const ROLE_OPTIONS = Object.keys(ROLE_LABEL) as ClubRole[];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type ClubOption = {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
};

export default function AdminAddClubMember() {
  const navigate = useNavigate();
  const { isAdmin: canAccess, loading: checkingAccess } = useIsAppAdmin();

  const [clubs, setClubs] = useState<ClubOption[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(true);

  const [clubId, setClubId] = useState("");
  const [userInput, setUserInput] = useState("");
  const [role, setRole] = useState<ClubRole>("joueur");

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;

    async function loadClubs() {
      setLoadingClubs(true);
      const { data, error } = await supabase
        .from("clubs")
        .select("id, name, city, country")
        .order("name", { ascending: true });

      if (!cancelled) {
        if (!error && data) {
          setClubs(data as ClubOption[]);
          if (data.length > 0) setClubId((prev) => prev || data[0].id);
        }
        setLoadingClubs(false);
      }
    }

    loadClubs();

    return () => {
      cancelled = true;
    };
  }, [canAccess]);

  const selectedClub = useMemo(
    () => clubs.find((c) => c.id === clubId) || null,
    [clubs, clubId]
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    const raw = userInput.trim();
    if (!clubId) {
      setErrorMsg("Choisis un club.");
      return;
    }
    if (!raw) {
      setErrorMsg("Indique le pseudo ou l'identifiant (UUID) de l'utilisateur.");
      return;
    }

    setSaving(true);

    try {
      let targetUserId: string | null = null;
      let targetPseudo: string | null = null;

      if (UUID_RE.test(raw)) {
        targetUserId = raw;
        const { data } = await supabase
          .from("user_profiles")
          .select("id, pseudo")
          .eq("id", raw)
          .maybeSingle();
        targetPseudo = data?.pseudo ?? null;
      } else {
        const { data, error } = await supabase
          .from("user_profiles")
          .select("id, pseudo")
          .eq("pseudo", raw)
          .maybeSingle();
        if (error) throw error;
        if (!data?.id) {
          throw new Error("Utilisateur introuvable (pseudo exact ou UUID).");
        }
        targetUserId = data.id;
        targetPseudo = data.pseudo;
      }

      if (!targetUserId) {
        throw new Error("Utilisateur introuvable (pseudo exact ou UUID).");
      }

      const { data: existing, error: existingError } = await supabase
        .from("club_members")
        .select("id")
        .eq("club_id", clubId)
        .eq("user_id", targetUserId)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existing?.id) {
        const { error: updateError } = await supabase
          .from("club_members")
          .update({ role, status: "active" })
          .eq("id", existing.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase.from("club_members").insert({
          club_id: clubId,
          user_id: targetUserId,
          role,
          status: "active",
          accepted_at: new Date().toISOString(),
        });
        if (insertError) throw insertError;
      }

      setSuccessMsg(
        `${targetPseudo ? `@${targetPseudo}` : targetUserId} a été ajouté à ${
          selectedClub?.name ?? "ce club"
        } en tant que ${ROLE_LABEL[role]}.`
      );
      setUserInput("");
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible d'associer cet utilisateur au club.");
    } finally {
      setSaving(false);
    }
  }

  if (checkingAccess) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Vérification des accès…
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <div className="font-semibold">Accès refusé</div>
              <div className="text-sm mt-1">
                Cette page est réservée aux administrateurs de l’application.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Espace admin</div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Associer un utilisateur à un club
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Ajoute un utilisateur existant directement dans un club, sans passer par une invitation.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
      </div>

      <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-gray-100 dark:bg-gray-800 p-3">
            <UserPlus className="h-6 w-6 text-gray-700 dark:text-gray-200" />
          </div>

          <div>
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Ajout direct
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              L'utilisateur est identifié par son <span className="font-medium">pseudo</span> (exact)
              ou son <span className="font-medium">UUID</span>. S'il est déjà membre du club, son rôle
              sera simplement mis à jour.
            </div>

            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1 text-xs text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
              <ShieldCheck className="h-3.5 w-3.5" />
              Action réservée aux administrateurs
            </div>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6 space-y-5"
      >
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Club
          </label>
          <div className="mt-2">
            <select
              value={clubId}
              onChange={(e) => setClubId(e.target.value)}
              disabled={saving || loadingClubs}
              className="w-full px-3 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
            >
              {loadingClubs && <option value="">Chargement des clubs…</option>}
              {!loadingClubs && clubs.length === 0 && (
                <option value="">Aucun club trouvé</option>
              )}
              {clubs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.city ? ` — ${c.city}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Utilisateur (pseudo ou UUID)
          </label>
          <div className="mt-2 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Ex. jdupont ou 3f2a1b4c-..."
              className="w-full pl-9 pr-3 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              disabled={saving}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Rôle dans le club
          </label>
          <div className="mt-2">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as ClubRole)}
              disabled={saving}
              className="w-full px-3 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {errorMsg && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/10 dark:text-emerald-200">
            {successMsg}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || !clubId || !userInput.trim()}
            className={cx(
              "inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-medium",
              "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
              (saving || !clubId || !userInput.trim()) && "opacity-60 cursor-not-allowed"
            )}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            {saving ? "Association..." : "Associer au club"}
          </button>
        </div>
      </form>
    </div>
  );
}
